import React, { useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Trash2 } from 'lucide-react';
import { computeBlockColor } from './colorUtils';

interface EditableLabelProps {
  value: string;
  color: string;
  total: number;
  onChange: (newValue: string) => void;
  onDelete?: () => void;
  canDelete?: boolean;
}

export function EditableLabel({ value, color, total, onChange, onDelete, canDelete }: EditableLabelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Compute the effective color based on the label
  const { color: effectiveColor, isLight } = computeBlockColor(value, color);

  const handleStartEditing = () => {
    setEditValue(value);
    setIsEditing(true);
    // Use setTimeout to ensure the input is visible before focusing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  };

  const handleFinishEditing = () => {
    if (editValue.trim()) {
      onChange(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFinishEditing();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the edit mode
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      className="tw-flex tw-items-center tw-gap-2"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Color swatch container with delete button overlay */}
      <div className="tw-relative tw-w-3 tw-h-3 tw-flex-shrink-0">
        {/* Color swatch - always rendered */}
        <div
          className="tw-absolute tw-inset-0"
          style={{
            backgroundColor: effectiveColor,
            borderRadius: 'var(--radius-sm)',
            // For light colors, add a visible border
            border: isLight ? '1px solid var(--border)' : 'none',
            opacity: isHovering && canDelete ? 0.3 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />

        {/* Delete button - overlays the swatch when hovering */}
        {isHovering && canDelete && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleDelete}
                className="tw-absolute tw-bg-white tw-text-neutral-500 hover:tw-text-red-600 tw-transition-colors tw-cursor-pointer"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--elevation-sm)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <Trash2 className="tw-w-3.5 tw-h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <span className="tw-text-white">Delete {value || 'Value'}</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Display mode */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded tw-whitespace-nowrap ${
              isEditing ? 'tw-hidden' : 'tw-flex tw-items-center tw-gap-1'
            }`}
            onClick={handleStartEditing}
            style={{
              borderRadius: 'var(--radius-sm)',
              backgroundColor: isHovering ? 'var(--primary)' : 'transparent',
              transition: 'background-color 0.2s ease-in-out',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-atkinson)',
                color: isHovering ? 'var(--primary-foreground)' : 'var(--foreground)',
                transition: 'color 0.2s ease-in-out',
              }}
            >
              {value}: {total}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="tw-text-white">Edit Label</span>
        </TooltipContent>
      </Tooltip>

      {/* Edit mode */}
      <div className={`tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap ${isEditing ? 'tw-flex' : 'tw-hidden'}`}>
        <Input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleFinishEditing}
          onKeyDown={handleKeyDown}
          className="tw-h-8 tw-px-2 tw-py-1 tw-w-32"
          style={{ borderRadius: 'var(--radius)' }}
        />
        <span className="tw-text-neutral-950">: {total}</span>
      </div>
    </div>
  );
}
